import { useState } from "react";

const roles = [
  "Software Engineer", "Mobile Developer", "AI/ML Engineer", "Cybersecurity Analyst",
  "Cloud Architect", "Digital Marketing Specialist", "Data Analyst", "IT Consultant",
  "Project Manager", "UI/UX Designer", "Other",
];
const experienceLevels = ["0–1 years", "1–3 years", "3–5 years", "5–10 years", "10+ years"];

type FormData = { name: string; email: string; phone: string; role: string; experience: string; linkedin: string; portfolio: string; message: string; };
const initial: FormData = { name: "", email: "", phone: "", role: "", experience: "", linkedin: "", portfolio: "", message: "" };

export default function CareersForm() {
  const [form, setForm] = useState<FormData>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) { setError("Message must be 500 characters or less."); return; }
    setError(""); setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d79ubtr2hsar0k5m0tqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ ...form }).toString(),
      });
      setSubmitted(true);
    } catch (_e) { setError("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="py-20 bg-gray-50" id="careers">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 border border-orange-200 rounded-full px-4 py-1.5 mb-4">
            <span className="text-orange-500 text-xs font-semibold uppercase tracking-wider">Join Our Team</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            Apply to <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">CPI Tech</span>
          </h2>
          <p className="text-gray-500 text-sm">We&apos;re always looking for talented people. Send us your details and we&apos;ll be in touch.</p>
        </div>

        {/* Perks */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { icon: "ri-home-office-line", label: "Remote Friendly" },
            { icon: "ri-heart-pulse-line", label: "Health Benefits" },
            { icon: "ri-graduation-cap-line", label: "Learning Budget" },
            { icon: "ri-trophy-line", label: "Competitive Pay" },
          ].map((p) => (
            <div key={p.label} className="bg-white rounded-xl p-4 flex flex-col items-center gap-2 text-center">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-50">
                <i className={`${p.icon} text-orange-500`} />
              </div>
              <span className="text-xs font-semibold text-gray-700">{p.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-gray-950 rounded-2xl p-8">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/20 mb-4">
                <i className="ri-check-line text-emerald-400 text-2xl" />
              </div>
              <h3 className="text-xl font-black text-white mb-2">Application Submitted!</h3>
              <p className="text-gray-400 text-sm">Thanks for applying! We&apos;ll review your application and reach out to <strong className="text-white">{form.email}</strong> within 5 business days.</p>
            </div>
          ) : (
            <form data-readdy-form id="careers-application-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Phone</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Role Applying For *</label>
                  <select name="role" value={form.role} onChange={handleChange} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer">
                    <option value="" className="bg-gray-900">Select a role</option>
                    {roles.map((r) => <option key={r} value={r} className="bg-gray-900">{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Years of Experience *</label>
                <select name="experience" value={form.experience} onChange={handleChange} required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer">
                  <option value="" className="bg-gray-900">Select experience level</option>
                  {experienceLevels.map((l) => <option key={l} value={l} className="bg-gray-900">{l}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">LinkedIn Profile</label>
                  <input type="url" name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Portfolio / GitHub</label>
                  <input type="url" name="portfolio" value={form.portfolio} onChange={handleChange} placeholder="https://github.com/..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">
                  Why CPI Tech? * <span className="text-gray-600">({form.message.length}/500)</span>
                </label>
                <textarea name="message" value={form.message} onChange={handleChange} required maxLength={500} rows={4}
                  placeholder="Tell us why you want to join CPI Tech and what you bring to the team..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors resize-none" />
              </div>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin" /> Submitting...</> : <><i className="ri-send-plane-line" /> Submit Application</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
